import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';

import { GraphDataByDate, GraphDataDto } from '../../../models/measurement.model';
import { DATE_COLORS, REFERENCE_COLORS } from '../chart-colors.constants';
import { MeasurementsGraphSeriesContext } from '../models/graph-series.models';

@Injectable()
export class MeasurementsGraphSeriesFactory {
  public createSeries(
    data: GraphDataDto,
    context: MeasurementsGraphSeriesContext,
  ): Highcharts.SeriesOptionsType[] {
    if (!data?.dataByDate || !Array.isArray(data.dataByDate)) {
      return [];
    }

    const dateSeries = data.dataByDate.flatMap((dateData, index) =>
      this.createMeasurementSeries(dateData, index, context),
    );

    return [
      ...dateSeries,
      this.createGrowthCurveAreaSeries(data, context),
      this.createAverageGrowthCurveSeries(data, context),
    ];
  }

  private createMeasurementSeries(
    dateData: GraphDataByDate,
    index: number,
    context: MeasurementsGraphSeriesContext,
  ): Highcharts.SeriesOptionsType[] {
    const color = DATE_COLORS[index];
    const isFirstSeries = index === 0;
    const cowName = dateData.measurements.find((cow) => !!cow.name)?.name ?? '';
    const individualMeasurementsBase = context.translatePipe.transform(
      'height-measurement.monitoring.graph.individualMeasurements',
    );
    const excludedMeasurementsBase = context.translatePipe.transform(
      'height-measurement.monitoring.graph.excludedMeasurements',
    );
    const trendLineBase = context.translatePipe.transform(
      'height-measurement.monitoring.graph.trendLine',
    );
    const individualMeasurementsLabel = context.isIndividualMonitoring
      ? `${individualMeasurementsBase} ${cowName}`.trim()
      : individualMeasurementsBase;
    const excludedMeasurementsLabel = context.isIndividualMonitoring
      ? `${excludedMeasurementsBase} ${cowName}`.trim()
      : excludedMeasurementsBase;
    const trendLineLabel = context.isIndividualMonitoring
      ? `${trendLineBase} ${cowName}`.trim()
      : trendLineBase;

    const measurementData = dateData.measurements
      .filter(
        (cow) =>
          (context.isIndividualMonitoring || !cow.isExcludedFromMeasurements) && cow.heightCm,
      )
      .map((cow) => ({
        cowId: cow.cowId,
        x: cow.ageInMonths,
        y: cow.heightCm,
        name: cow.name,
        earNumber: cow.earNumber,
        registrationNumber: cow.registrationNumber,
        bodyConditionScore: cow.bodyConditionScore,
        isExcludedFromMeasurements: cow.isExcludedFromMeasurements,
        measurementDate: dateData.measurementDate,
      }));

    const excludedData = dateData.measurements
      .filter(
        (cow) => !context.isIndividualMonitoring && cow.isExcludedFromMeasurements && cow.heightCm,
      )
      .map((cow) => ({
        cowId: cow.cowId,
        x: cow.ageInMonths,
        y: cow.heightCm,
        name: cow.name,
        earNumber: cow.earNumber,
        registrationNumber: cow.registrationNumber,
        bodyConditionScore: cow.bodyConditionScore,
        isExcludedFromMeasurements: cow.isExcludedFromMeasurements,
        measurementDate: dateData.measurementDate,
      }));

    return [
      {
        type: 'scatter',
        id: isFirstSeries ? 'measurements' : undefined,
        linkedTo: isFirstSeries ? undefined : 'measurements',
        className: 'measurements-legend-series',
        name: individualMeasurementsLabel,
        color: color.primary,
        showInLegend: isFirstSeries,
        data: measurementData,
        marker: { radius: 4 },
        zIndex: 1000 - index,
      } as Highcharts.SeriesOptionsType,
      {
        type: 'scatter',
        id: isFirstSeries ? 'excluded' : undefined,
        linkedTo: isFirstSeries ? undefined : 'excluded',
        className: 'excluded-legend-series',
        name: excludedMeasurementsLabel,
        color: color.excluded,
        showInLegend: isFirstSeries && !context.isIndividualMonitoring,
        data: excludedData,
        marker: { radius: 4, symbol: 'circle', lineWidth: 2 },
        zIndex: 900 - index,
      } as Highcharts.SeriesOptionsType,
      {
        type: 'spline',
        id: isFirstSeries ? 'trendline' : undefined,
        linkedTo: isFirstSeries ? undefined : 'trendline',
        className: 'trendline-legend-series',
        name: trendLineLabel,
        color: color.primary,
        showInLegend: isFirstSeries,
        data: dateData.trendLine,
        enableMouseTracking: false,
        marker: { enabled: false },
        lineWidth: 2,
      } as Highcharts.SeriesOptionsType,
    ];
  }

  private createGrowthCurveAreaSeries(
    data: GraphDataDto,
    context: MeasurementsGraphSeriesContext,
  ): Highcharts.SeriesOptionsType {
    const growthCurveRangeKey = context.isIndividualMonitoring
      ? 'height-measurement.monitoring.graph.growthCurveRangeSolo'
      : 'height-measurement.monitoring.graph.growthCurveRange';

    return {
      type: 'arearange',
      name: `${context.translatePipe.transform(growthCurveRangeKey)}`,
      data: data.upperGrowthCurve.map((point, index) => [
        point.x,
        point.y,
        data.lowerGrowthCurve[index].y,
      ]),
      enableMouseTracking: false,
      fillOpacity: 0.2,
      lineWidth: 0,
      color: REFERENCE_COLORS.growthCurveRange,
      marker: { enabled: false },
    } as Highcharts.SeriesOptionsType;
  }

  private createAverageGrowthCurveSeries(
    data: GraphDataDto,
    context: MeasurementsGraphSeriesContext,
  ): Highcharts.SeriesOptionsType {
    return {
      type: 'spline',
      name: `${context.translatePipe.transform('height-measurement.monitoring.graph.averageGrowthCurve')}`,
      color: REFERENCE_COLORS.averageGrowthCurve,
      dashStyle: 'Dash',
      data: data.averageGrowthCurve.map((point) => [point.x, point.y]),
      enableMouseTracking: false,
      marker: { enabled: false },
    } as Highcharts.SeriesOptionsType;
  }
}
